import { LocalReminderActionModal } from '@/components/mirror/LocalReminderActionModal';
import { MIRROR_REMINDER_CARDS_ENABLED } from '@/lib/featureFlags';
import { reminderOriginOperationId, type LocalReminderActionPrefill } from '@/lib/reminderExecutor';
import { reminderId } from '../../../../supabase/functions/_shared/reminderActionDomain';
import type { CaptureContext, ModuleCaptureHandler } from '@/lib/capture/types';

const object = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);
const validId = (value: unknown): value is string => { try { reminderId(value); return true; } catch { return false; } };

async function origin(ctx: CaptureContext, actionId: string, action: LocalReminderActionPrefill['action'], input: Record<string, unknown>): Promise<LocalReminderActionPrefill | null> {
  // Proposal fields are drafts. Snapshots, ownership, timestamps and operation
  // IDs are obtained independently and cannot be supplied by model content.
  const args = structuredClone(input), owner = ctx.ownerId, sessionId = ctx.sessionId;
  if (!validId(owner) || !validId(sessionId)) return null;
  const assert = () => ctx.assertCurrent?.(); assert();
  const operation_id = await reminderOriginOperationId(owner, sessionId, actionId, 'operation', assert); assert();
  const reminder_id = action === 'create'
    ? await reminderOriginOperationId(owner, sessionId, actionId, 'reminder', assert)
    : validId(args.reminder_id) ? reminderId(args.reminder_id) : undefined;
  assert();
  if (action !== 'set_preferences' && !reminder_id) return null;
  const fields = action === 'set_preferences' ? ['preferences']
    : action === 'delete' ? [] : action === 'set_enabled' ? ['enabled']
    : ['category', 'message', 'schedule_type', 'schedule_config', 'enabled'];
  const draft = Object.fromEntries(Object.entries(args).filter(([key]) => fields.includes(key)));
  return { ...draft, action, action_id: actionId, operation_id, ...(reminder_id ? { reminder_id } : {}), expected_user_id: owner, expected_session_id: sessionId } as LocalReminderActionPrefill;
}

export const reminderCaptureHandler: ModuleCaptureHandler<LocalReminderActionPrefill> = {
  tool: 'propose_reminder_action', isEnabled: () => MIRROR_REMINDER_CARDS_ENABLED,
  getRawText: proposal => proposal.tool === 'propose_reminder_action' ? JSON.stringify(proposal.args) : '',
  extract: async (_raw, proposal, ctx) => {
    if (proposal.tool !== 'propose_reminder_action' || !['create', 'update', 'delete', 'set_enabled'].includes(proposal.args.action)) return null;
    return origin(ctx, proposal.action_id, proposal.args.action, proposal.args as unknown as Record<string, unknown>);
  },
  renderModal: props => <LocalReminderActionModal {...props} />,
};

export const reminderPreferencesCaptureHandler: ModuleCaptureHandler<LocalReminderActionPrefill> = {
  tool: 'propose_reminder_preferences_action', isEnabled: () => MIRROR_REMINDER_CARDS_ENABLED,
  getRawText: proposal => proposal.tool === 'propose_reminder_preferences_action' ? JSON.stringify(proposal.args) : '',
  extract: async (_raw, proposal, ctx) => proposal.tool === 'propose_reminder_preferences_action' && object(proposal.args.preferences)
    ? origin(ctx, proposal.action_id, 'set_preferences', { preferences: proposal.args.preferences }) : null,
  renderModal: props => <LocalReminderActionModal {...props} />,
};
